// Brand availability helpers — used by QRMenu, MultiBrandOrder, and BrandsIndex.
// Single source of truth for "is this brand orderable right now?".

const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

/**
 * Parse "HH:MM" into total minutes since midnight. Returns null on invalid.
 */
const parseTime = (s) => {
    if (!s || typeof s !== "string") return null;
    const m = s.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const h = parseInt(m[1], 10);
    const mn = parseInt(m[2], 10);
    if (isNaN(h) || isNaN(mn) || h < 0 || h > 23 || mn < 0 || mn > 59) return null;
    return h * 60 + mn;
};

const LONDON_FMT = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", weekday: "long", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
});
const londonParts = (date) => {
    const p = Object.fromEntries(LONDON_FMT.formatToParts(date).map((x) => [x.type, x.value]));
    return { dayIdx: DAY_KEYS.indexOf(p.weekday.toLowerCase()), nowMins: Number(p.hour) * 60 + Number(p.minute) };
};

const fmt = (mins) => {
    if (mins == null) return "";
    const h = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/**
 * Given a brand and (optionally) a Date, return its current availability.
 *
 *   { available: true }                        → normal, orders allowed
 *   { available: false, reason: "coming_soon" }→ show "Opening soon" splash
 *   { available: false, reason: "closed",
 *     nextOpen: "17:00", nextOpenLabel: "Opening at 17:00" } → show splash
 *   { available: false, reason: "closed",
 *     nextOpen: null,   nextOpenLabel: "Closed today" }      → no times set for today
 */
export const brandOpenState = (brand, now = new Date()) => {
    if (!brand) return { available: false, reason: "closed", nextOpenLabel: "Unavailable" };
    if (brand.coming_soon) return { available: false, reason: "coming_soon", nextOpenLabel: "Opening soon" };
    if (brand.temporarily_closed && (!brand.reopen_at || new Date(brand.reopen_at) > now)) {
        const back = brand.reopen_at
            ? new Date(brand.reopen_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })
            : null;
        return { available: false, reason: "closed_now", nextOpenLabel: back ? `Closed · back ${back}` : "Closed right now" };
    }

    const hours = brand.opening_hours || {};
    // No opening hours configured at all → treat as always open (backwards compatible)
    if (!hours || Object.keys(hours).length === 0) return { available: true };

    // Always evaluate in kitchen time (Europe/London) so devices abroad match the server.
    const { dayIdx, nowMins } = londonParts(now);

    // Check TODAY first
    // Yesterday's past-midnight window still running (e.g. Fri 18:00 → 02:00, now Sat 01:00)
    const yest = hours[DAY_KEYS[(dayIdx + 6) % 7]];
    if (yest && !yest.closed && yest.open && yest.close) {
        const yo = parseTime(yest.open), yc = parseTime(yest.close);
        if (yo != null && yc != null && yc <= yo && nowMins < yc) return { available: true };
    }

    const today = hours[DAY_KEYS[dayIdx]];
    if (today && !today.closed && today.open && today.close) {
        const openMins = parseTime(today.open);
        const closeMins = parseTime(today.close);
        if (openMins != null && closeMins != null) {
            // Support past-midnight closes (e.g. 23:00 open, 02:00 close)
            const withinWindow = closeMins > openMins
                ? nowMins >= openMins && nowMins < closeMins
                : nowMins >= openMins;
            if (withinWindow) return { available: true };
            // Not open yet today?
            if (nowMins < openMins) {
                return {
                    available: false,
                    reason: "closed",
                    nextOpen: today.open,
                    nextOpenLabel: `Opening at ${today.open}`,
                };
            }
        }
    }

    // Look forward up to 7 days for the next open slot
    for (let i = 1; i <= 7; i++) {
        const d = hours[DAY_KEYS[(dayIdx + i) % 7]];
        if (d && !d.closed && d.open) {
            const dayLabel = i === 1 ? "tmrw" : DAY_KEYS[(dayIdx + i) % 7].slice(0, 3);
            return {
                available: false,
                reason: "closed",
                nextOpen: d.open,
                nextOpenLabel: `Opens ${d.open} ${dayLabel}`,
            };
        }
    }

    return { available: false, reason: "closed", nextOpenLabel: "Closed today" };
};

export const DAY_LABELS = [
    { key: "monday", label: "Mon" },
    { key: "tuesday", label: "Tue" },
    { key: "wednesday", label: "Wed" },
    { key: "thursday", label: "Thu" },
    { key: "friday", label: "Fri" },
    { key: "saturday", label: "Sat" },
    { key: "sunday", label: "Sun" },
];


/**
 * Sort brands so open ones appear at the top, closed-for-now next, and
 * coming-soon at the bottom. Within each bucket the original order is
 * preserved so admin ordering / drag-drop still wins.
 *
 * Buckets (lower priority index = higher up):
 *   0 → open right now
 *   1 → closed for now (opens later today OR opens on a later day)
 *   2 → coming_soon (not launched yet)
 */
export const sortBrandsByAvailability = (brands, now = new Date()) => {
    if (!Array.isArray(brands)) return [];
    const bucketOf = (b) => {
        const a = brandOpenState(b, now);
        if (a.available) return 0;
        if (a.reason === "coming_soon") return 2;
        return 1;
    };
    // stable sort by bucket; keep original index as tiebreaker.
    return brands
        .map((b, i) => ({ b, i, k: bucketOf(b) }))
        .sort((a, z) => (a.k - z.k) || (a.i - z.i))
        .map(({ b }) => b);
};
